<script setup lang="ts">
import { ref, watch, toRef } from 'vue'
import GwMessage from '@/components/Messages/GwMessage.vue'
import OpenChatLink from '@/components/Payments/OpenChatLink.vue'
import { t } from '@/locales/i18n'

interface FormCreationErrorParams {
  error: object | string
  endpoint?: string
}

interface AdyenError {
  error: boolean
  message?: string
  status?: string
}

interface Props {
  formCreationError: FormCreationErrorParams | null
  adyenError: AdyenError
}

const props = defineProps<Props>()
const errorCopied = ref(false)

const copyToClipboard = (formCreationError: FormCreationErrorParams) => {
  const str = JSON.stringify({
    error: JSON.stringify(
      formCreationError.error,
      Object.getOwnPropertyNames(formCreationError.error),
    ),
    url: window.location.href,
    endpoint: formCreationError.endpoint || '',
  })
  navigator.clipboard.writeText(str)
  errorCopied.value = true
  setTimeout(() => {
    errorCopied.value = false
  }, 5000)
}

// Using this variable and the watch is the hack to show the error message on transition
// Otherwise when the error notification hides, the error message is not visible
const lastVisibleAdyenErrorMessage = ref<string | undefined>(undefined)
const adyenError = toRef(props, 'adyenError')

watch(adyenError, newVal => {
  if (newVal.message) {
    lastVisibleAdyenErrorMessage.value = newVal.message
  }
})
</script>

<template>
  <div>
    <div
      v-if="formCreationError"
      style="background: var(--clr-error); border-radius: 8px"
      class="pa-4 mb-4"
    >
      <div class="grow text-small weight_500 text-center">
        <span v-html="t('payment.errors.form_creation')" />
        <OpenChatLink />.
        <div>
          <div
            class="mt-2 text-smallest"
            style="max-height: 3rem; overflow: auto"
          >
            ---
            {{ formCreationError }}
            ---
          </div>
          <div
            class="mt-2 gw_btn mx-auto"
            style="--clr-back: var(--clr-white-0); --clr: var(--clr-black-2); --width: 50%"
            @click="copyToClipboard(formCreationError)"
          >
            {{ t('payment.errors.copy_error') }}
          </div>
          <div
            v-if="errorCopied"
            class="pt-2"
          >
            {{ t('payment.errors.error_copied') }}
          </div>
        </div>
      </div>
    </div>

    <GwMessage
      :message="{
        type: 'error',
        text: lastVisibleAdyenErrorMessage,
        hide: true,
      }"
      :style="{
        maxHeight: adyenError.error ? '100px' : '0',
        borderWidth: adyenError.error ? 'unset' : '0',
        transition: 'max-height 0.3s ease-in-out, border-width 0.2s ease-in-out',
      }"
    >
      <template
        v-if="adyenError.error"
        #default
      >
        <div v-if="adyenError.message">
          {{ adyenError.message }}
        </div>
        <div v-else>
          {{
            adyenError.status === 'CARD_DECLINED'
              ? t('payment.error.status_stripe_CARD_DECLINED')
              : t('payment.error.status_stripe_UNKNOWN_ERROR')
          }}
          <OpenChatLink v-if="adyenError.status !== 'CARD_DECLINED'" />
        </div>
      </template>
    </GwMessage>
  </div>
</template>
